import React from "react";
import "./store-category.css";
import { Link } from 'react-router-dom';
import { CategoryGridSkeleton, StoreHeaderSkeleton } from "../SkeletonLoader";

const StoreCategory = ({
  storeDetail = {},
  categories,
  storeLoading = false,
  categoriesLoading = false,
  businessSegmentId = "",
  segmentId = "",
  segment,
  storeId = "",
  showStoreHeader = true,
  searchTerm = "",
}) => {
  const categoryItems = Array.isArray(categories?.cell_contents) ? categories.cell_contents : [];
  const hasStoreDetail = storeDetail && Object.keys(storeDetail).length > 0;

  const getValue = (item, keys) => {
    const matchedKey = keys.find(
      (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
    );

    return matchedKey ? item[matchedKey] : "";
  };

  const slugify = (value, fallback = "category") =>
    String(value || fallback)
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || fallback;

  const getCategoryId = (category) =>
    getValue(category, ["category_id", "categoryId", "id", "product_category_id"]);

  const toTitleCase = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

  const getCategoryTitle = (category, index) =>
    toTitleCase(category?.title || category?.name || category?.category_name || `Category ${index + 1}`);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const displayedCategories = categoryItems.map((category, index) => ({ category, index })).filter(
    ({ category, index }) => getCategoryTitle(category, index).toLowerCase().includes(normalizedSearchTerm)
  );

  const getCategoryImage = (category) =>
    category?.image || category?.category_image || "assets/images/thumbs/product-two-img1.png";

  const getSelectedBusinessSegmentId = () =>
    businessSegmentId ||
    getValue(storeDetail, ["business_segment_id", "businessSegmentId", "store_id", "storeId", "id"]);

  const getStoreImage = () =>
    storeDetail?.background_image ||
    storeDetail?.cover_image ||
    storeDetail?.image ||
    "assets/images/thumbs/vendor-logo1.png";

  const storeTitle = storeDetail?.title || storeDetail?.name || "Store";
  const storeRating = storeDetail?.rating || storeDetail?.avg_rating || "4.5";
  const storeTime = storeDetail?.time || storeDetail?.delivery_time || "90 min";
  const storeDistance = storeDetail?.distance || storeDetail?.store_distance || "";

  if (!showStoreHeader && !categoriesLoading && categoryItems.length === 0) {
    return null;
  }

  const getCategoryPath = (category, index) => {
    const categoryId = getCategoryId(category);
    const categorySlug = `${slugify(getCategoryTitle(category, index))}${categoryId ? `-${categoryId}` : ""}`;
    const selectedBusinessSegmentId = getSelectedBusinessSegmentId();
    const storeSlug = `${slugify(storeDetail?.title || storeDetail?.name || "store")}${
      selectedBusinessSegmentId ? `-${selectedBusinessSegmentId}` : ""
    }`;

    return selectedBusinessSegmentId
      ? `/shop/${encodeURIComponent(storeSlug)}/${encodeURIComponent(categorySlug)}`
      : `/shop/${encodeURIComponent(categorySlug)}`;
  };

  return (
    <div className="store-page">
      <div className="container container-lg">
        {/* STORE HEADER */}
        {showStoreHeader && storeLoading ? (
          <StoreHeaderSkeleton />
        ) : showStoreHeader && hasStoreDetail ? (
          <div className="store-category-header">
            <img
              src={getStoreImage()}
              alt={storeTitle}
              className="store-category-header__image"
            />

            <div className="store-category-header__content">
              <div className="store-category-header__title-row">
                <h5>{storeTitle}</h5>
                <span className="store-category-header__rating">
                  <i className="ph-fill ph-star" /> {storeRating}
                </span>
              </div>
              {storeDetail.address ? (
                <p className="store-category-header__meta store-category-header__meta--address">
                  <i className="ph ph-map-pin" />
                  <span>{storeDetail.address}</span>
                </p>
              ) : null}
              <p className="store-category-header__meta">
                <i className="ph ph-clock" />
                <span>{storeTime}</span>
              </p>
              {storeDistance ? (
                <p className="store-category-header__meta">
                  <i className="ph ph-navigation-arrow" />
                  <span>{storeDistance}</span>
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* CATEGORIES */}
        {categoriesLoading ? (
          <CategoryGridSkeleton />
        ) : categoryItems.length > 0 || normalizedSearchTerm ? (
          <>
            <h6 className="store-category-title">{categories?.cell_title_text || "Categories"}</h6>

            {displayedCategories.length > 0 ? <div className="store-category-grid">
              {displayedCategories.map(({ category: cat, index: i }) => (
                <div className="store-category-card" key={i}>
                  <Link
                    className="store-category-card__link"
                    to={getCategoryPath(cat, i)}
                    state={{
                      category: cat,
                      categoryId: getCategoryId(cat),
                      parentCategoryId: getCategoryId(cat),
                      businessSegmentId: getSelectedBusinessSegmentId(),
                      segmentId: segmentId || getValue(cat, ["segment_id", "segmentId", "segment"]),
                      segment,
                      storeId,
                      store: storeDetail,
                    }}
                  >
                    <span className="store-category-card__thumb">
                      <img src={getCategoryImage(cat)} alt={getCategoryTitle(cat, i)} />
                    </span>
                    <span className="store-category-card__title">{getCategoryTitle(cat, i)}</span>
                  </Link>
                </div>
              ))}
            </div> : <div className="store-category-search-empty" role="status">No categories found. Try another search.</div>}
          </>
        ) : null}

      </div>
    </div>
  );
};

export default StoreCategory;
